import { useState } from 'react';
import { TableCell, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AlarmWarningIcon, SitesIcon } from 'src/assets';
import {
  StyledTableRow,
  tableCell,
  tableCellBox,
  iconSiteBox,
  siteType,
  iconAlarmBox,
} from './style';
import { Address, NewSite } from 'src/features/sites/types/sites';
import { formatSiteType } from 'src/features/sites/utils';
import { SitesService } from 'src/services/sites';
import { useSite } from 'src/contexts/siteContext/sites.context';
import { SiteInterface } from 'src/features/sites/types/site';
import { useNavigate } from 'react-router';
import { SITES_DASHBOARD_PATH, SITES_MANAGEMENT_PATH } from 'src/routes/config';
import { useAuthentication } from 'src/features/authentication/context';
import { WarningIcon, InfoIcon } from 'src/features/theme/overrides/CustomIcons';
import { AlarmPriorityType } from 'src/features/alarm-rules/types';
import { GetDirectionsButton } from '../../get-directions-button/GetDirectionsButton';

type TableRowItemTypes = {
  row?: NewSite;
  selected?: boolean;
  testId?: string;
  onSelectRow: VoidFunction;
};

export const TableRowItem = ({ row, selected, onSelectRow }: TableRowItemTypes) => {
  const { saveSite } = useSite();
  const {
    customerId: { value: customerId },
  } = useAuthentication();
  const navigate = useNavigate();
  const theme = useTheme();
  const greyColor = theme.palette.grey[500];
  const mainColor = theme.palette.primary.main;

  const address = new Address(row?.location);

  const [iconColor, setIconColor] = useState(greyColor);

  const onMouseEnter = () => {
    setIconColor(mainColor);
  };
  const onMouseLeave = () => {
    setIconColor(greyColor);
  };

  const handleRedirect = (siteId: string) => {
    if (!customerId) return;
    navigate(SITES_DASHBOARD_PATH + `?customerId=${customerId}&siteId=${siteId}`);
  };

  const handleRowClick = () => {
    if (!customerId) return;
    SitesService.getById({ customerId: customerId, siteId: row!.id })
      .then((data: SiteInterface) => {
        saveSite(data);
      })
      .catch((error) => console.error(error));
    handleRedirect(row!.id);
    onSelectRow();
  };

  const handleAlarmView = (priority: AlarmPriorityType) => {
    switch (priority) {
      case 'Critical':
        return <AlarmWarningIcon fill={theme.palette.error.main} />;
      case 'Warning':
        return <WarningIcon htmlColor={theme.palette.warning.main} />;
      case 'Info':
        return <InfoIcon htmlColor={theme.palette.secondary.main} />;
      default:
        return '-';
    }
  };

  const onMiddleMouseClick = (event: any) => {
    event.preventDefault();
    if (event.button === 1) {
      const currentLocation = window.location.href;
      let newLocation = '';
      if (currentLocation.includes('siteId')) {
        const locationSplit = currentLocation.split('siteId');
        newLocation =
          locationSplit[0].replace(SITES_MANAGEMENT_PATH, SITES_DASHBOARD_PATH) +
          `siteId=${row?.id}`;
      } else {
        newLocation =
          currentLocation.replace(SITES_MANAGEMENT_PATH, SITES_DASHBOARD_PATH) +
          `&siteId=${row?.id}`;
      }

      window.open(newLocation, '_blank', 'noopener');
      window.blur();
    }
  };

  return (
    <>
      <StyledTableRow
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        theme={theme}
        hover
        selected={selected}
        data-sm="table-row"
        onMouseDown={onMiddleMouseClick}
      >
        <TableCell data-sm="site-name-row" sx={tableCell} onClick={handleRowClick}>
          <Box sx={tableCellBox}>
            <Box sx={iconSiteBox}>
              <SitesIcon fill={iconColor} />
            </Box>
            <Typography variant="subtitle2">{row?.siteName}</Typography>
          </Box>
        </TableCell>

        <TableCell data-sm="site-type-row" sx={tableCell} onClick={handleRowClick}>
          <Typography sx={siteType} variant="subtitle2">
            {formatSiteType(row?.siteType!)}
          </Typography>
        </TableCell>
        <TableCell data-sm="address-row" sx={tableCell}>
          <Typography variant="subtitle2">{`${address}`}</Typography>
        </TableCell>
        <TableCell sx={tableCell}>
          <GetDirectionsButton row={row} />
        </TableCell>
        {
          <TableCell data-sm="open-issues-row" sx={tableCell}>
            <Box sx={tableCellBox}>
              <Box sx={iconAlarmBox}>{handleAlarmView(row?.highestActiveAlarm!)}</Box>
              <Typography variant="subtitle2">{row?.highestActiveAlarm && 'Alarm'}</Typography>
            </Box>
          </TableCell>
        }
      </StyledTableRow>
    </>
  );
};
